import type { DetectionProfile } from '../core/types';

/**
 * Preserves the included 85-record synthetic demonstration dataset's v1 behavior,
 * while expressing all limits as versioned, inspectable rules.
 */
export const includedBaselineProfile: DetectionProfile = {
  id: 'included-baseline',
  version: '1.0.0',
  label: 'Included Baseline',
  description: "Synthetic profile for the repository's included telemetry dataset.",
  synthetic: true,
  dataClassification: 'SYNTHETIC_UNCLASSIFIED',
  platformCategory: 'included-baseline',
  expectedCadenceMs: 10_000,
  cadenceToleranceMs: 1_000,
  staleAfterMs: 30_000,
  sequencePolicy: 'optional',
  channels: {
    altitude: {
      channel: 'altitude',
      label: 'Altitude',
      unit: 'ft',
      required: true,
      minimum: 0,
      maximum: 60_000,
    },
    speed: {
      channel: 'speed',
      label: 'Speed',
      unit: 'kts',
      required: true,
      minimum: 0,
      maximum: 900,
    },
    fuel: { channel: 'fuel', label: 'Fuel', unit: '%', required: true, minimum: 0, maximum: 100 },
  },
  rules: [
    {
      id: 'core.range.altitude',
      kind: 'range',
      label: 'Altitude range',
      description: 'Checks that synthetic altitude remains within the declared profile range.',
      severity: 'error',
      enabled: true,
      channel: 'altitude',
      minimum: 0,
      maximum: 60_000,
    },
    {
      id: 'core.range.speed',
      kind: 'range',
      label: 'Speed range',
      description: 'Checks that synthetic speed remains within the declared profile range.',
      severity: 'error',
      enabled: true,
      channel: 'speed',
      minimum: 0,
      maximum: 900,
    },
    {
      id: 'core.range.fuel',
      kind: 'range',
      label: 'Fuel range',
      description: 'Checks that synthetic fuel percentage remains between zero and one hundred.',
      severity: 'error',
      enabled: true,
      channel: 'fuel',
      minimum: 0,
      maximum: 100,
    },
    {
      id: 'core.rate.altitude',
      kind: 'rate',
      label: 'Altitude rate',
      description: 'Detects implausible changes using elapsed time rather than sample count.',
      severity: 'warning',
      enabled: true,
      channel: 'altitude',
      maximumAbsoluteRate: 250,
    },
    {
      id: 'core.rate.speed',
      kind: 'rate',
      label: 'Speed rate',
      description: 'Detects implausible speed changes using elapsed time.',
      severity: 'warning',
      enabled: true,
      channel: 'speed',
      maximumAbsoluteRate: 15,
    },
    {
      id: 'core.rate.fuel',
      kind: 'rate',
      label: 'Fuel rate validity',
      description: 'Detects extreme fuel signal changes outside the diagnostic event rule.',
      severity: 'error',
      enabled: true,
      channel: 'fuel',
      maximumAbsoluteRate: 1,
    },
    {
      id: 'baseline.overspeed',
      kind: 'threshold',
      label: 'Synthetic overspeed condition',
      description: "Preserves the included demonstration's speed condition.",
      severity: 'warning',
      enabled: true,
      channel: 'speed',
      operator: '>',
      threshold: 520,
    },
    {
      id: 'baseline.rapid-descent',
      kind: 'window-decrease',
      label: 'Synthetic rapid-descent condition',
      description: 'Detects altitude decreases across a real 20-second window.',
      severity: 'error',
      enabled: true,
      channel: 'altitude',
      maximumDecrease: 900,
      windowMs: 20_000,
      toleranceMs: 0,
    },
    {
      id: 'baseline.fuel-change',
      kind: 'decrease-rate',
      label: 'Synthetic fuel-change condition',
      description: 'Preserves the included fuel event while accounting for actual elapsed time.',
      severity: 'warning',
      enabled: true,
      channel: 'fuel',
      maximumDecreaseRate: 0.2,
    },
    {
      id: 'sensor.altitude.frozen',
      kind: 'frozen',
      label: 'Frozen altitude sensor',
      description: 'Detects a sustained unchanging altitude channel.',
      severity: 'warning',
      enabled: true,
      channel: 'altitude',
      minimumDurationMs: 30_000,
      tolerance: 0.001,
    },
    {
      id: 'sensor.speed.frozen',
      kind: 'frozen',
      label: 'Frozen speed sensor',
      description: 'Detects a sustained unchanging speed channel.',
      severity: 'warning',
      enabled: true,
      channel: 'speed',
      minimumDurationMs: 30_000,
      tolerance: 0.001,
    },
    {
      id: 'sensor.fuel.frozen',
      kind: 'frozen',
      label: 'Frozen fuel sensor',
      description: 'Detects a sustained unchanging fuel channel.',
      severity: 'warning',
      enabled: true,
      channel: 'fuel',
      minimumDurationMs: 30_000,
      tolerance: 0.001,
    },
  ],
};
